import {STREAM_AUDIO_KEY, useGuideAudioController} from "@khan/guide-core";
import {highlightSourceText} from "@khan/guide-math-editor";
import type {Message} from "@khan/guide-types";

interface TextToSpeechProps {
    /**
     * The text of the message, possibly with highlighting.
     */
    formattedMessageText: string;
    /**
     * Whether the message has audio.
     */
    hasAudio: boolean;
    /**
     * Whether the message's audio is currently playing from the audio cache.
     */
    isOwnAudioPlaying: boolean;
    /**
     * Whether the message's audio is currently playing from the stream.
     */
    isOwnAudioPlayingFromStream: boolean;
}

/**
 * Used to get common props for a message related to text-to-speech.
 *
 * @param message - The message.
 * @param isMostRecentMessage - Whether the message is the most recent message in the conversation.
 */
export const useTextToSpeechPropsForMessage = (
    message: Message,
    isMostRecentMessage: boolean,
): TextToSpeechProps => {
    const {audioCache, audioKey, chunkIndex, isPlaying} =
        useGuideAudioController();
    const ownAudioKey = message.interactionId;
    const hasAudio = audioCache.has(ownAudioKey);

    // Once all audio is received, the audio is assigned to the message's
    // interactionId (the audioKey).
    const isOwnAudioPlaying = hasAudio && isPlaying && audioKey === ownAudioKey;

    // While the audio is streaming, we play from the stream buffer. If the
    // message is the most recent message, we highlight the text as it is being
    // played.
    const isOwnAudioPlayingFromStream =
        isPlaying &&
        audioKey === STREAM_AUDIO_KEY &&
        (!!message.isGeneratingResponse || isMostRecentMessage);

    let text = message.text;
    if (isOwnAudioPlaying) {
        const audioChunks = audioCache.get(ownAudioKey) ?? [];
        text = highlightSourceText(message.text, audioChunks, chunkIndex);
    }

    if (isOwnAudioPlayingFromStream) {
        const audioChunks = audioCache.get(STREAM_AUDIO_KEY) ?? [];
        text = highlightSourceText(message.text, audioChunks, chunkIndex);
    }

    return {
        formattedMessageText: text,
        hasAudio,
        isOwnAudioPlaying,
        isOwnAudioPlayingFromStream,
    };
};
